<template>
  <InformRmmModal v-if="showInformRmmModal"
    @close="showInformRmmModal = false"
    :visible="showInformRmmModal"
    :customer="customer"
  />

  <LimitExceededModal
    v-if="showLimitExceededModal"
    @close="showLimitExceededModal = false"
    :visible="showLimitExceededModal"
    title="Quota Limit Exceeded"
    message="You have exceeded the maximum orderable jerseys for your program. The quantity has been adjusted to stay within your quota. Please contact byip@timhortons.com if you have questions."
  />

  <LimitExceededModal
    v-if="showInventoryLimitModal"
    @close="showInventoryLimitModal = false"
    :visible="showInventoryLimitModal"
    title="Inventory Limit Exceeded"
    message="The quantity you tried to copy exceeds the available inventory for this jersey variant. The field was populated up to the maximum available inventory."
  />

  <MedalReminderModal
    v-if="showMedalReminderModal"
    :customer="customer"
    @close="showMedalReminderModal = false"
    @add-medals="showMedalReminderModal = false; scrollToMedals()"
    @continue-without-medals="showMedalReminderModal = false; showConfirmOrderModal = true"
    :visible="showMedalReminderModal"
  />

  <!-- Jerseys Selection Start -->
  <div class="row">
    <div class="col-12">
      <div class="row">
        <!-- AgeGroup Slider, Size Guide, and Jerseys Selection Page -->
        <div v-if="!showCheckoutPage && currentStepName === 'jerseys'" class="col-12 col-lg-9">
          <div class="card tim-card radius-20">
            <div class="card-content">
              <div class="order-form">
                <!-- <CategoryBullets :customer="customer" /> --> <!-- TODO Remove if not needed Or Place the AgeGroup BreadCrumbs here-->

                <!-- Place Your Order & Age Group Selection Dropdown Sections -->
                <div class="col-12 d-flex justify-content-between align-items-center mb-2 p-0">
                  <div class="d-flex gap-2 align-items-center">
                    <!-- Go Back to Start Your Order Page -->
                    <a href="customer_views" class="back-btn">
                      <img :src="require('../images/svgs/back_arrow_icon.svg')" alt="Back Arrow Icon">
                    </a>
                    <h3 class="m-0 order-flow-title fs-26">{{ customer.translations.place_your_order }}</h3>
                  </div>

                  <div class="jersey-options w-100" style="max-width: 217px;">
                    <div class="age-dropdown" @focusout="onAgeDropdownBlur">
                      <button type="button" :class="['styled-dropdown', 'text-left', { 'has-value': selectedAgeGroup, 'open': ageDropdownOpen }]" @click="toggleAgeDropdown">
                        {{ customer.translations[selectedAgeGroup] }}
                      </button>
                      <div v-if="ageDropdownOpen" class="age-dropdown-menu">
                        <input ref="ageSearchInput" v-model="ageGroupSearch" type="text" class="age-dropdown-search">
                        <ul class="age-dropdown-list list-unstyled m-0">
                          <li v-for="ageGroup in filteredAgeGroups" :key="ageGroup"
                              :class="['age-dropdown-option', { 'selected': ageGroup === selectedAgeGroup }]"
                              @mousedown.prevent="selectAgeGroupOption(ageGroup)">
                            {{ customer.translations[ageGroup] }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Age Group Selection Slider Section -->
                <div class="row">
                  <div class="col-12">
                    <ul class="list-unstyled category-bullet p-0 gap-2 my-32 ff-mont">
                      <li v-for="(ageGroup, index) in visibleAgeGroups" :key="index">
                        <a :class="['bullet-link', { 'active-link': activeAgeGroupIndex >= index }]" @click="updateSelectedAgeGroup(ageGroup)">
                          <div class="bullet-icon">
                            <img :src="require('../images/svgs/jersey_no_7_icon.svg')" alt="Jersey No 7 Icon | AgeGroup">
                          </div>
                          <span>{{ customer.translations[ageGroup] }}</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>

                <div v-if="shouldShowProductTabs" class="product-tabs mb-3">
                  <div class="product-tab-switch">
                    <a
                      id="new-products-tab"
                      class="product-tab-option"
                      :class="{ 'is-active': activeProductTab === 'new' }"
                      href="#"
                      @click.prevent="setActiveProductTab('new')"
                    >
                      <span class="product-tab-dot"></span>
                      <span class="product-tab-label">{{ customer.translations.new_products || 'New Products' }}</span>
                      <span v-if="activeProductTab === 'new'" class="product-tab-badge">{{ customer.translations.current || 'Current' }}</span>
                    </a>
                    <a
                      id="old-products-tab"
                      class="product-tab-option"
                      :class="{ 'is-active': activeProductTab === 'old' }"
                      href="#"
                      @click.prevent="setActiveProductTab('old')"
                    >
                      <i class="fa fa-history product-tab-icon"></i>
                      <span class="product-tab-label">{{ customer.translations.old_products || 'Old Products' }}</span>
                      <span v-if="activeProductTab === 'old'" class="product-tab-badge">{{ customer.translations.current || 'Current' }}</span>
                    </a>
                  </div>
                </div>

                <!-- Team Breakdown Section -->
                <div id="age-group-size-breakdown" class="team-breakdown-section mb-4">
                  <h4 class="order-subtitle mb-0">{{ customer.translations.age_group_size_breakdown }}</h4>
                  <div class="table-scroll-x">
                    <table class="table-striped table light-bg m-0 order-table radius-20 colored-th-table colored-table-odd mt-3">
                      <thead>
                        <tr>
                          <th class="text-center">Age Group: {{ customer.translations[selectedAgeGroup] }}</th>
                          <th class="text-center" v-for="variant in displayVariants" :key="variant.size">{{ variant.size }}</th>
                          <th class="text-center">{{ customer.translations.total }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <p class="mb-0 text-center">{{ customer.translations.quantities }}</p>
                          </td>
                          <td v-for="variant in displayVariants" :key="variant.size">
                            <div class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1">
                              <button type="button" class="no-btn" @click="decrementTeamBreakdownQuantity(variant.size)">-</button>

                              <input type="number" class="w-100 text-center team-breakdown-input"
                                v-model.number="teamBreakdownQuantities[selectedAgeGroup][variant.size]"
                                @input="validateTeamBreakdownInput(variant.size)"
                                min="0" />

                              <button type="button" class="no-btn" @click="incrementTeamBreakdownQuantity(variant.size)">+</button>
                            </div>
                          </td>
                          <td>
                            <div class="mx-auto mb-1 align-items-center justify-content-center" style="text-align: center;">
                              <span class="fs-16">
                                {{ teamBreakdownTotal(selectedAgeGroup) }}
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Jerseys' Size Guide Section -->
                <div class="jersey-selection d-flex justify-content-between mb-1 align-items-end">
                  <h4 class="order-subtitle mb-0">{{ customer.translations.jerseys }}</h4>

                  <button v-if="!customer.show_old_products" class="guide-btn no-btn m-0" @click="openModal">
                    <img :src="require('../images/svgs/size_guide_ruler.svg')" alt="Size Guide Ruler">
                    {{ customer.translations.show_size_guide }}
                  </button>

                  <SizeGuideModal
                    :visible="isModalVisible"
                    :currentUser="currentUser"
                    :customer="customer"
                    :vendor="vendor"
                    :type="'jerseys'"
                    @close="isModalVisible = false"
                    :enabledSportsSite="enabledSportsSite"
                  />
                </div>

                <!-- Jerseys Products Selection table -->
                <div class="table-scroll-x">
                <table class="table-striped table light-bg m-0 order-table order-table-edit radius-20 colored-th-table colored-table-odd">
                  <thead>
                    <tr>
                      <th>{{ customer.translations.colour }}</th>
                      <th v-for="variant in displayVariants">{{ variant.size }}</th>
                      <th>{{ customer.translations.total }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(jersey, index) in displayJerseys" :key="jersey.id" :id="`jersey_${jersey.id}`">
                      <!-- Jerseys Image, and Color/Title -->
                      <td>
                        <div class="d-flex justify-content-center align-items-center">
                          <!-- Image + Title + Button -->
                          <div class="d-flex flex-column gap-1 align-items-center">
                            <img :src="jersey.image_url" class="product-img mb-1" width="74" alt="Jersey Image" />
                            <p style="color: #4F2D2CE5; font-size: 11px;" class="mb-1 text-center">
                              {{ customer.soccer_sports ? jersey.color : jersey.title }}
                            </p>

                            <!-- Copy Button Below -->
                            <button
                              :id="getFirstJerseyId() === jersey.id ? 'apply-team-sizes' : null"
                              class="btn-copy-from-above btn-copy-small"
                              @click="copyJerseyQuantitiesFromAbove(index)"
                            >
                              {{ customer.translations.apply_team_sizes }}
                            </button>
                          </div>
                        </div>
                      </td>

                      <td v-for="variant in jersey.variants" :key="variant.id">
                        <div v-if="matchingJerseyVariant(jersey, variant.size)">
                          <div class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1" v-if="hasInventory(variant)">
                            <button v-if="hasInventory(variant)" type="button" class="no-btn" @click="decrementJerseyQuantity(jersey.id, variant.id)">-</button>

                            <input type="number" class="w-100 text-center"
                              :id="variantInputId(jersey, variant)"
                              @input="validateInput('jerseys',jersey.id, variant.id, variant.quantity)"
                              v-model="localJerseyVariantQuantities[selectedAgeGroup][jersey.id].variants[variant.id]"
                              @blur="checkValidations(jersey.id, variant.id, 'Jerseys')"
                              min="0"
                              :max="variant.quantity"
                            />

                            <button v-if="hasInventory(variant)" type="button" class="no-btn" @click="incrementJerseyQuantity(jersey.id, variant.id)">+</button>
                          </div>

                          <!-- Renders this if Jerseys Not available or Out of Stock -->
                          <div v-else class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1">
                            <input type="text" class="w-100 text-center" disabled
                              :id="variantInputId(jersey, variant)"
                              value="-"
                            />
                          </div>

                          <!-- Available Quantity or Out of Stock Text -->
                          <p v-if="hasInventory(variant)" class="inventory-count m-0">{{ variant.quantity }} {{ customer.translations.available }}</p>
                          <p v-else class="inventory-count m-0">{{ customer.translations.out_of_stock }}</p>
                        </div>

                        <!-- Jersey is Sold out -->
                        <div v-else>
                          <div class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8">
                            -
                          </div>
                          <p class="inventory-count m-0">{{ customer.translations.sold_out }}</p>
                        </div>
                      </td>

                      <td>
                        <p id="`${product.id}_${variant.id}_total_jersey`">{{ getJerseyTotalQuantity(jersey.id) }}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right side - Jerseys Order Summary, Selected Jerseys and their Variants Table-->
        <div v-if="!showCheckoutPage && currentStepName === 'jerseys'" class="col-12 col-lg-3 pl-lg-0">
          <div class="card radius-20 summary-order">
            <div class="card-content">
              <h3 class="order-title">{{ customer.translations.order_summary }}</h3>
              <div class="d-flex flex-wrap row-gap-3 summary">
                <div class="d-flex w-50 align-items-center gap-1">
                  <img :src="require('../images/svgs/person-icon.svg')" alt="Person Icon">
                  <div>
                    <h5>Associate Name</h5>
                    <p class="details">{{ customer.club_name }}</p>
                  </div>
                </div>

                <div class="d-flex w-50 align-items-center gap-1" v-if="customer.mfjd_age_group">
                  <img :src="require('../images/svgs/multi-persons-icon.svg')" alt="Two Persons Icon">

                  <div>
                    <h5>{{ customer.translations.group_name }}: </h5>
                    <p class="details">My First Jersey ({{ customer.mfjd_age_group }})</p>
                  </div>
                </div>

                <div class="d-flex w-50 align-items-center gap-1">
                  <img :src="require('../images/svgs/vendor-icon.svg')" alt="Vendor Shop Icon">

                  <div>
                    <h5>{{ customer.translations.vendor }}: </h5>
                    <p class="details" v-if="vendor && vendor.name">{{ vendor.name }}</p>
                  </div>
                </div>
              </div>

              <!-- quantity -->
              <div class="d-flex radius-8 light-bg my-3 w-100 text-center">
                <div class="p-1 w-50" style="border-right: 1px solid #DDC8C433;">
                  <h4 class="sum-value">
                    {{ enabledSportsSite === 'Soccer' ? customer.soccer_jerseys_quota : customer.hockey_jerseys_quota }}
                  </h4>
                  <h6 class="sum-title">{{ customer.translations.maximum_quantity }}</h6>
                </div>

                <div class="x-seprator"></div>
                <div class="p-1 w-50">
                  <h4 id="jersey-total-quantity" class="sum-value">{{ getAllJerseysTotalQuantity() }}</h4>
                  <h6 class="sum-title">{{ customer.translations.ordered }}</h6>
                </div>
              </div>

              <!-- Selected Jerseys and their Variants Table -->
              <template v-for="ageGroup in customer.age_groups.flat()">
                <div v-if="isTotalAgeGroupQuantityPresent(ageGroup)" class="table-responsive mb-3">
                  Jerseys: {{ ageGroup }}
                  <table class="table light-bg m-0 order-table radius-8 summary-table">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th v-for="variant in getSummaryVariants()" :key="variant.id">{{ variant.size }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="jersey in getSummaryJerseys()">
                        <tr v-if="hasJerseyQuantityInAgeGroup(ageGroup, jersey.id)">
                          <td>
                            <div class="d-flex flex-column gap-1 align-items-center">
                              <img :src="jersey.image_url" class="product-img" width="74" alt="Jersey Image" />
                              <p style="color: #4F2D2CE5; font-size: 11px;">
                                {{ customer.soccer_sports ? jersey.color : jersey.title }}
                              </p>
                            </div>
                          </td>
                          <td v-for="variant in getSummaryVariants()">
                            {{getVariantQuantitySummary(jersey, variant, ageGroup)}}
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </template>

              <!-- Next Age Group and Next Step Buttons-->
              <div class="d-flex mb-1 gap-2">
                <button href="javascript:void(0)" class="btn outlined-primary-btn px-1 w-50" @click="nextAgeGroup()">{{ customer.translations.next_age_group }}</button>

                <button class="btn primary-btn px-1 w-50" @click="nextStep">{{ customer.translations.next }}</button>
              </div>

              <!-- Cancel Order -->
              <button class="text-center btn modal-secondary-btn w-100" @click="openCancelOrderModal">{{ customer.translations.cancel_order }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Jerseys Selection End -->

  <!-- Logo Selection Start -->
  <div v-if="!showCheckoutPage && currentStepName === 'logo selection'">
    <LogoSelection
      :currentStepIndex="localStepIndex"

      :currentUser="currentUser"
      :customer="customer"
      :vendor="vendor"
      :rmm="rmm"

      :jerseys="jerseys"
      :variants="variants"

      :shorts="shorts"
      :shortsVariants="shortsVariants"

      :socks="socks"
      :socksVariants="socksVariants"

      :oldSocks="oldSocks"
      :oldSocksVariants="oldSocksVariants"

      :newSocks="newSocks"
      :newSocksVariants="newSocksVariants"

      :balls="balls"
      :ballsVariants="ballsVariants"

      :medals="medals"
      :medalsVariants="medalsVariants"

      :enabledSportsSite="enabledSportsSite"
      :orderImage="orderImage"

      @selectYes="showLogoUploadView"
      @selectNo="handleNoSelection"

      :ageGroups="localAgeGroups"

      :totalJerseysOrdered="localTotalJerseysOrdered"
      :jerseyVariantQuantities="localJerseyVariantQuantities"

      :totalShortsOrdered="localTotalShortsOrdered"
      :shortsVariantQuantities="localShortsVariantQuantities"

      :totalSocksOrdered="localTotalSocksOrdered"
      :socksVariantQuantities="localSocksVariantQuantities"

      :totalBallsOrdered="localTotalBallsOrdered"
      :ballsVariantQuantities="localBallsVariantQuantities"

      :totalMedalsOrdered="localTotalMedalsOrdered"
      :medalsVariantQuantities="localMedalsVariantQuantities"
    />
  </div>
  <!-- Logo Selection End -->

  <!-- Other Products/Accessories Section Start -->
  <div v-if="!showCheckoutPage && currentStepName === 'other products'">
    <div class="col-12">
      <div class="row">
        <!-- Left side page. Shorts, Socks, Balls, and Medals Section -->
        <div class="col-12 col-lg-9">
          <div class="card tim-card radius-20">
            <!-- Shorts, Socks, Balls, and Medals Section -->

            <!-- Reminder to Order Medals -->
            <div class="alert alert-warning no-autodismiss text-center" role="alert">
              {{ customer.translations.do_not_forget_medal }}
            </div>

            <br>

            <div class="card-content">
              <div v-if="enabledSportsSite === 'Soccer'">
                <div v-if="!isMonetaryProgram">
                  <!-- Size Guide Shorts -->
                  <div class="jersey-selection d-flex justify-content-between mb-1 align-items-end">
                    <h4 class="order-subtitle mb-0 fw-600 uppercase" style="color: #09101D;">{{ customer.translations.shorts }}</h4>

                    <button class="guide-btn no-btn m-0" @click="openModal">
                      <img :src="require('../images/svgs/size_guide_ruler.svg')" alt="Size Guide Ruler Icon">
                      {{ customer.translations.show_size_guide }}
                    </button>

                    <SizeGuideModal
                      :visible="isModalVisible"
                      :currentUser="currentUser"
                      :customer="customer"
                      :vendor="vendor"
                      :type="'shorts'"
                      @close="isModalVisible = false"
                      :enabledSportsSite="enabledSportsSite"
                    />
                  </div>

                  <!-- Shorts Selection Table Start -->
                  <div class="table-responsive">
                  <table class="table-striped table light-bg m-0 order-table order-table-edit radius-20 colored-th-table ">
                    <thead>
                      <tr>
                        <th class="fs-14">{{ customer.translations.colour }}</th>
                        <th class="fs-14" v-for="shortsVariant in sortedShortsVariants" :key="shortsVariant.id">{{ shortsVariant.size }}</th>
                        <th class="fs-14">{{ customer.translations.total }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="short in shorts" :key="short.id" :id="`short_${short.id}`">
                        <td>
                          <div class="d-flex flex-column gap-1 align-items-center">
                            <img :src="short.image_url" class="product-img" width="74" alt="Short Image" />
                            <p style="color: #4F2D2CE5; font-size: 11px;" class="mb-0">
                              {{ customer.soccer_sports ? short.color : short.title }}
                            </p>
                          </div>
                        </td>

                        <td v-for="shortsVariant in sortedShortsVariants" :key="shortsVariant.id">
                          <div v-if="matchingShortVariant(short, shortsVariant.size)">
                            <div class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1">
                              <button v-if="hasInventory(shortsVariant)" @click="decrementShortQuantity(short.id, shortsVariant.id)" type="button" class="no-btn">-</button>
                              <input
                                  type="number"
                                  class="w-100 text-center"
                                  :id="variantInputId(short, shortsVariant)"
                                  @input="validateInput('shorts', short.id, shortsVariant.id, shortsVariant.quantity)"
                                  v-model="localShortsVariantQuantities[short.id].variants[shortsVariant.id]"
                                  :value="localShortsVariantQuantities[short.id].variants[shortsVariant.id] || 0"
                                  @blur="checkValidations(short.id, shortsVariant.id, 'Shorts')"
                                  min="0"
                                  :disabled="!hasInventory(shortsVariant)"
                              />
                              <button v-if="hasInventory(shortsVariant)" @click="incrementShortQuantity(short.id, shortsVariant.id)" type="button" class="no-btn">+</button>
                            </div>
                            <p v-if="hasInventory(shortsVariant)" class="inventory-count m-0">{{ shortsVariant.quantity }} {{ customer.translations.available }}</p>
                            <p v-else class="inventory-count m-0">{{ customer.translations.out_of_stock }}</p>
                          </div>
                          <div v-else>
                            <div class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8">
                              -
                            </div>
                            <p class="inventory-count m-0">{{ customer.translations.sold_out }}</p>
                          </div>
                        </td>

                        <td>{{ getShortTotalQuantity(short.id) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                  <!-- Shorts Selection Table End -->
                </div>
              </div>

              <!-- Socks Selection Table Start -->
              <div v-if="!isMonetaryProgram">
                <div class="jersey-selection d-flex justify-content-between my-1 align-items-end">
                  <h4 class="order-subtitle mb-0 fw-600 uppercase" style="color: #09101D;">
                    {{ customer.translations.socks }}
                  </h4>
                </div>

                <div v-if="shouldShowSocksProductTabs" class="product-tabs mb-3">
                  <div class="product-tab-switch">
                    <a
                      id="new-socks-tab"
                      class="product-tab-option"
                      :class="{ 'is-active': activeSocksProductTab === 'new' }"
                      href="#"
                      @click.prevent="setActiveSocksProductTab('new')"
                    >
                      <span class="product-tab-dot"></span>
                      <span class="product-tab-label">{{ customer.translations.new_products || 'New Products' }}</span>
                      <span v-if="activeSocksProductTab === 'new'" class="product-tab-badge">{{ customer.translations.current || 'Current' }}</span>
                    </a>
                    <a
                      id="old-socks-tab"
                      class="product-tab-option"
                      :class="{ 'is-active': activeSocksProductTab === 'old' }"
                      href="#"
                      @click.prevent="setActiveSocksProductTab('old')"
                    >
                      <i class="fa fa-history product-tab-icon"></i>
                      <span class="product-tab-label">{{ customer.translations.old_products || 'Old Products' }}</span>
                      <span v-if="activeSocksProductTab === 'old'" class="product-tab-badge">{{ customer.translations.current || 'Current' }}</span>
                    </a>
                  </div>
                </div>

                <div class="table-responsive">
                <table
                  class="table-striped table light-bg m-0 order-table order-table-edit radius-20 colored-th-table"
                  style="table-layout: fixed; width: 100%;"
                >
                  <colgroup>
                    <col />
                    <col v-for="socksVariant in displaySocksVariants" :key="socksVariant.id" />
                    <col />
                  </colgroup>

                  <thead>
                    <tr>
                      <th style="text-align: center; vertical-align: middle;">
                        {{ customer.translations.colour }}
                      </th>

                      <th
                        v-for="socksVariant in displaySocksVariants"
                        :key="socksVariant.id"
                        style="text-align: center; vertical-align: middle;"
                      >
                        {{ socksVariant.size }}
                      </th>

                      <th style="text-align: center; vertical-align: middle;">
                        {{ customer.translations.total }}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr v-for="sock in displaySocks" :key="sock.id" :id="`sock_${sock.id}`">
                      <!-- Color Column -->
                      <td style="text-align: center; vertical-align: top;">
                        <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
                          <img
                            :src="sock.image_url"
                            width="74"
                            alt="sock Image"
                            style="max-width: 74px; display: block;"
                          />

                          <p style="color: #4F2D2CE5; font-size: 11px; margin: 0;">
                            {{ customer.soccer_sports ? sock.color : sock.title }}
                          </p>

                          <button
                            class="btn-autofill-socks"
                            style="font-size: 11px; max-width: 100%;"
                            @click="autoFillSocks(sock.id)"
                          >
                          {{ customer.translations.autofill_from_jersey_count }}
                          </button>
                        </div>
                      </td>

                      <!-- Size Columns -->
                      <td
                        v-for="socksVariant in sock.variants"
                        :key="socksVariant.id"
                        style="text-align: center; vertical-align: top;"
                      >
                        <div v-if="matchingSocksVariant(sock, socksVariant.size)">
                          <div
                            class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1"
                            style="min-width: 90px;"
                          >
                            <button
                              v-if="hasInventory(socksVariant)"
                              @click="decrementSockQuantity(sock.id, socksVariant.id)"
                              type="button"
                              class="no-btn"
                            >
                              -
                            </button>

                            <input
                              type="number"
                              class="w-100 text-center"
                              style="min-width: 40px;"
                              :id="variantInputId(sock, socksVariant)"
                              @input="validateInput('socks', sock.id, socksVariant.id, socksVariant.quantity)"
                              v-model="localSocksVariantQuantities[sock.id].variants[socksVariant.id]"
                              @blur="checkValidations(sock.id, socksVariant.id, 'Socks')"
                              min="0"
                              :disabled="!hasInventory(socksVariant)"
                            />

                            <button
                              v-if="hasInventory(socksVariant)"
                              @click="incrementSockQuantity(sock.id, socksVariant.id)"
                              type="button"
                              class="no-btn"
                            >
                              +
                            </button>
                          </div>

                          <p style="margin: 0; font-size: 11px;">
                            {{ socksVariant.quantity }} {{ customer.translations.available }}
                          </p>
                        </div>

                        <div v-else>
                          <div
                            class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8"
                            style="min-width: 90px;"
                          >
                            -
                          </div>
                          <p style="margin: 0; font-size: 11px;">
                            {{ customer.translations.sold_out }}
                          </p>
                        </div>
                      </td>

                      <!-- Total Column -->
                      <td style="text-align: center; vertical-align: top; font-weight: 600;">
                        {{ getSockTotalQuantity(sock.id) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
              <!-- Socks Selection Table End -->

              <!-- Balls Selection Table Start -->
              <div v-if="enabledSportsSite === 'Soccer'">
                <div class="jersey-selection d-flex justify-content-between my-1 align-items-end">
                  <h4 class="order-subtitle mb-0 fw-600 uppercase" style="color: #09101D;">{{ customer.translations.soccer_balls }}</h4>
                </div>

                <div class="table-responsive">
                <table
                  class="table-striped table light-bg m-0 order-table order-table-edit radius-20 colored-th-table"
                  style="table-layout: fixed; width: 100%;"
                >
                  <colgroup>
                    <col />
                    <col v-for="ballsVariant in ballsVariants" :key="ballsVariant.id" />
                    <col />
                  </colgroup>

                  <thead>
                    <tr>
                      <th style="text-align: center; vertical-align: middle;">
                        {{ customer.translations.colour }}
                      </th>

                      <th
                        v-for="ballsVariant in ballsVariants"
                        :key="ballsVariant.id"
                        style="text-align: center; vertical-align: middle;"
                      >
                        {{ ballsVariant.size }}
                      </th>

                      <th style="text-align: center; vertical-align: middle;">
                        {{ customer.translations.total }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ball in balls" :key="ball.id" :id="`ball_${ball.id}`">
                      <td style="text-align: center; vertical-align: top;">
                        <div style="display: flex; flex-direction: column; align-items: center; gap: 4px;">
                          <img
                            :src="ball.image_url"
                            width="74"
                            alt="ball Image"
                            style="max-width: 74px; display: block;"
                          />

                          <p style="color: #4F2D2CE5; font-size: 11px; margin: 0;">
                            {{ customer.soccer_sports ? ball.color : ball.title }}
                          </p>

                          <button
                            class="btn-autofill-socks"
                            style="font-size: 11px; max-width: 100%;"
                            @click="autoFillBalls"
                          >
                          {{ customer.translations.autofill_from_jersey_count }}
                          </button>
                        </div>
                      </td>

                      <td
                        v-for="ballsVariant in ballsVariants"
                        :key="ballsVariant.id"
                        style="text-align: center; vertical-align: top;"
                      >
                        <div v-if="matchingBallsVariant(ball, ballsVariant.size)">
                          <div
                            class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1"
                            style="min-width: 90px;"
                          >
                            <button
                              v-if="hasInventory(ballsVariant)"
                              @click="decrementBallQuantity(ball.id, ballsVariant.id)"
                              type="button"
                              class="no-btn"
                            >
                              -
                            </button>

                            <input
                              type="number"
                              class="w-100 text-center"
                              style="min-width: 40px;"
                              min="0"
                              @input="validateInput('balls', ball.id, ballsVariant.id, ballsVariant.quantity)"
                              @blur="checkValidations(ball.id, ballsVariant.id, 'Balls')"
                              :disabled="!hasInventory(ballsVariant)"
                              v-model="localBallsVariantQuantities[ball.id].variants[ballsVariant.id]"
                            />

                            <button
                              v-if="hasInventory(ballsVariant)"
                              @click="incrementBallQuantity(ball.id, ballsVariant.id)"
                              type="button"
                              class="no-btn"
                            >
                              +
                            </button>
                          </div>

                          <p style="margin: 0; font-size: 11px;">
                            {{ ballsVariant.quantity }} {{ customer.translations.available }}
                          </p>
                        </div>
                        <div v-else>
                          <div
                            class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8"
                            style="min-width: 90px;"
                          >
                            -
                          </div>
                          <p style="margin: 0; font-size: 11px;">
                            {{ customer.translations.sold_out }}
                          </p>
                        </div>
                      </td>

                      <td style="text-align: center; vertical-align: top; font-weight: 600;">
                        {{ getBallTotalQuantity(ball.id) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              </div>
              <!-- Balls Selection Table End -->

              <!-- Medals Selection Table Start -->
              <div class="jersey-selection d-flex justify-content-between my-1 align-items-end">
                <h4 class="order-subtitle mb-0 fw-600" style="color: rgb(9, 16, 29);">{{ customer.translations.medals }}</h4>
              </div>

              <div class="table-responsive">
              <table
                class="table-striped table light-bg m-0 order-table order-table-edit radius-20 colored-th-table"
                style="table-layout: fixed; width: 100%;"
              >
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>

                <thead>
                  <tr>
                    <th style="text-align:center; vertical-align:middle;" class="fs-14">
                      {{ customer.translations.colour }}
                    </th>
                    <th style="text-align:center; vertical-align:middle;" class="fs-14">
                      {{ customer.translations.medal }}
                    </th>
                    <th style="text-align:center; vertical-align:middle;" class="fs-14">
                      {{ customer.translations.total }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <template v-for="medalVariant in medalsVariants">
                    <tr v-for="medal in medals">
                      <!-- ENGLISH MEDALS -->
                      <template v-if="medalVariant.size == 'ENGLISH' && canShowEnglishMedals">
                        <td style="text-align:center; vertical-align:top;">
                          <div style="display:flex; flex-direction:column; align-items:center; gap:4px;">
                            <img
                              :src="medal.image_url"
                              width="74"
                              alt="Medal Image"
                              style="max-width:74px; display:block;"
                            />
                            <button
                              class="btn-autofill-socks"
                              style="font-size:11px; max-width:100%;"
                              @click="autoFillMedals('ENGLISH')"
                            >
                            {{ customer.translations.autofill_from_jersey_count }}
                            </button>
                          </div>
                        </td>

                        <td style="text-align:center; vertical-align:top;">
                          <div v-if="matchingMedalsVariant(medal, medalVariant.size)">
                            <p style="margin:0;">{{ medalVariant.size }}</p>

                            <div
                              class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1"
                              style="min-width:90px;"
                            >
                              <button
                                v-if="hasInventory(medalVariant)"
                                @click="decrementMedalQuantity(medal.id, medalVariant.id)"
                                type="button"
                                class="no-btn"
                              >
                                -
                              </button>

                              <input
                                type="number"
                                class="w-100 text-center"
                                style="min-width:40px;"
                                min="0"
                                @input="validateInput('medals', medal.id, medalVariant.id, medalVariant.quantity)"
                                @blur="checkValidations(medal.id, medalVariant.id, 'Medals')"
                                :disabled="!hasInventory(medalVariant)"
                                :id="variantInputId(medal, medalVariant)"
                                v-model="localMedalsVariantQuantities[medal.id].variants[medalVariant.id]"
                              />

                              <button
                                v-if="hasInventory(medalVariant)"
                                @click="incrementMedalQuantity(medal.id, medalVariant.id)"
                                type="button"
                                class="no-btn"
                              >
                                +
                              </button>
                            </div>

                            <p style="margin:0; font-size:11px;">
                              {{ medalVariant.quantity }} {{ customer.translations.available }}
                            </p>
                          </div>

                          <div v-else>
                            <div
                              class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8"
                              style="min-width:90px;"
                            >
                              -
                            </div>
                            <p style="margin:0; font-size:11px;">
                              {{ customer.translations.sold_out }}
                            </p>
                          </div>
                        </td>

                        <td style="text-align:center; vertical-align:top; font-weight:600;">
                          {{ getMedalTotalQuantity(medal.id, medalVariant.id) }}
                        </td>
                      </template>

                      <!-- FRENCH MEDALS -->
                      <template v-if="medalVariant.size == 'FRENCH' && canShowFrenchMedals">
                        <td style="text-align:center; vertical-align:top;">
                          <div style="display:flex; flex-direction:column; align-items:center; gap:4px;">
                            <img
                              :src="medal.french_medal_image.url"
                              width="74"
                              alt="Medal Image"
                              style="max-width:74px; display:block;"
                            />
                            <button
                              class="btn-autofill-socks"
                              style="font-size:11px; max-width:100%;"
                              @click="autoFillMedals('FRENCH')"
                            >
                            {{ customer.translations.autofill_from_jersey_count }}
                            </button>
                          </div>
                        </td>

                        <td style="text-align:center; vertical-align:top;">
                          <div v-if="matchingMedalsVariant(medal, medalVariant.size)">
                            <p style="margin:0;">{{ medalVariant.size }}</p>

                            <div
                              class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1"
                              style="min-width:90px;"
                            >
                              <button
                                v-if="hasInventory(medalVariant)"
                                @click="decrementMedalQuantity(medal.id, medalVariant.id)"
                                type="button"
                                class="no-btn"
                              >
                                -
                              </button>

                              <input
                                type="number"
                                class="w-100 text-center"
                                style="min-width:40px;"
                                min="0"
                                @input="validateInput('medals', medal.id, medalVariant.id, medalVariant.quantity)"
                                @blur="checkValidations(medal.id, medalVariant.id, 'Medals')"
                                :disabled="!hasInventory(medalVariant)"
                                :id="variantInputId(medal, medalVariant)"
                                v-model="localMedalsVariantQuantities[medal.id].variants[medalVariant.id]"
                              />

                              <button
                                v-if="hasInventory(medalVariant)"
                                @click="incrementMedalQuantity(medal.id, medalVariant.id)"
                                type="button"
                                class="no-btn"
                              >
                                +
                              </button>
                            </div>

                            <p style="margin:0; font-size:11px;">
                              {{ medalVariant.quantity }} {{ customer.translations.available }}
                            </p>
                          </div>

                          <div v-else>
                            <div
                              class="d-flex align-items-center justify-content-center light-bg jersey-counter mx-auto mb-1 opacity-8"
                              style="min-width:90px;"
                            >
                              -
                            </div>
                            <p style="margin:0; font-size:11px;">
                              {{ customer.translations.sold_out }}
                            </p>
                          </div>
                        </td>

                        <td style="text-align:center; vertical-align:top; font-weight:600;">
                          {{ getMedalTotalQuantity(medal.id, medalVariant.id) }}
                        </td>
                      </template>
                    </tr>
                  </template>
                  <tr>
                    <td colspan="3">
                      <div class="d-flex justify-content-center w-75 mx-auto gap-2">
                        <div class="d-flex flex-column gap-2 justify-content-center align-items-center">
                          <img
                            :src="require('../../assets/images/optional_medal.png')"
                            class="product-img"
                            width="74"
                            alt="Medal Image"
                          />

                          <p class="d-flex gap-1" style="color: #996A13; font-family: 'Inter'; font-size: 12px;">
                            <img :src="require('../images/svgs/warning_sign.svg')" alt="Warning Sign Icon">
                            {{ customer.translations.representation_of_medal }}
                          </p>
                        </div>

                        <div class="text-left">
                          <div>
                            <div class="d-flex align-items-center gap-1 mb-1">
                              <div class="form-check d-flex flex-column gap-1">
                                <div>
                                  <input class="form-check-input" type="checkbox" id="optionallogo" :checked="localAddMylarLogoChecked" @click="updateMylarLogo($event)" />
                                  <label class="form-check-label" for="optionallogo"> {{ customer.translations.optional_mylar_logo }} </label>
                                </div>

                                <span class="charge-medal">{{ customer.translations.artwork_charge }}</span>
                              </div>

                              <img :src="require('../images/svgs/exclamation_sign.svg')" alt="Exclamation Sign">
                            </div>

                            <p style="color: #696F8C;">{{ customer.translations.please_note_this }}</p>
                            <p style="color: #696F8C;">{{ customer.translations.mylar_logo_not_funded }}</p>
                            <p style="color: #696F8C;">{{ customer.translations.mylar_desc_1_1 }}</p>
                            <p style="color: #696F8C;">{{ customer.translations.mylar_desc_2 }}</p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              </div>
              <!-- Medals Selection Table End -->
            </div>
          </div>
        </div>

        <!-- TODO Right now the Jerseys Variants Selected Template is duplicated to keep the UI consistent. Refactor later -->
        <div class="col-12 col-lg-3 pl-lg-0">
          <div class="card radius-20 summary-order">
            <!-- Other Products Summary Start -->
            <div class="card-content">
              <h3 class="order-title">{{ customer.translations.order_summary }}</h3>
            </div>

            <ul class="list-unstyled d-flex justify-content-between flex-wrap row-gap-3 p-0 my-32">
              <!-- Shorts (Soccer Only) -->

              <li class="w-50" v-if="enabledSportsSite === 'Soccer'">
                <div class="d-flex align-items-start basic-group gap-1">
                  <div class="summary-icon">
                    <img :src="require('../images/svgs/jersey-icon.svg')" alt="Shorts Icon">
                  </div>

                  <div>
                    <h6 class="title">{{ customer.translations.jerseys }}</h6>
                    <h3 class="value">{{ getAllJerseysTotalQuantity() }}</h3>
                    <p class="capacity">{{ customer.translations.maximum_quantity }} (<span>{{ customer.soccer_jerseys_quota }}</span>)</p>
                  </div>
                </div>
              </li>

              <li class="w-50" v-if="enabledSportsSite === 'Soccer'">
                <div class="d-flex align-items-start basic-group gap-1">
                  <div class="summary-icon">
                    <img :src="require('../images/svgs/shorts-icon.svg')" alt="Shorts Icon">
                  </div>

                  <div>
                    <h6 class="title">{{ customer.translations.shorts }}</h6>
                    <h3 class="value">{{ getAllShortsTotalQuantity() }}</h3>
                    <p class="capacity">{{ customer.translations.maximum_quantity }} (<span>{{ customer.soccer_shorts_quota }}</span>)</p>
                  </div>
                </div>
              </li>

              <!-- Socks (Both Soccer & Hockey) -->
              <li class="w-50" v-if="enabledSportsSite === 'Soccer' || enabledSportsSite === 'Hockey'">
                <div class="d-flex align-items-start basic-group gap-1">
                  <div class="summary-icon">
                    <img :src="require('../images/svgs/socks-icon.svg')" alt="Socks Icon">
                  </div>

                  <div>
                    <h6 class="title">{{ customer.translations.socks }}</h6>
                    <h3 class="value">{{ getAllSocksTotalQuantity() }}</h3>
                    <p class="capacity">
                      {{ customer.translations.maximum_quantity }}
                      (<span>{{ enabledSportsSite === 'Soccer' ? customer.soccer_socks_quota : customer.hockey_socks_quota }}</span>)
                    </p>
                  </div>
                </div>
              </li>

              <!-- Balls (Soccer Only) -->
              <li class="w-50" v-if="enabledSportsSite === 'Soccer'">
                <div class="d-flex align-items-start basic-group gap-1">
                  <div class="summary-icon">
                    <img :src="require('../images/svgs/balls-icon.svg')" alt="Balls Icon">
                  </div>
                  <div>
                    <h6 class="title">{{ customer.translations.balls }}</h6>
                    <h3 class="value">{{ getAllBallsTotalQuantity() }}</h3>
                    <p class="capacity">{{ customer.translations.maximum_quantity }} (<span>{{ customer.soccer_balls_quota }}</span>)</p>
                  </div>
                </div>
              </li>

              <!-- Medals (Both Soccer & Hockey) -->
              <li class="w-50" v-if="enabledSportsSite === 'Soccer' || enabledSportsSite === 'Hockey'">
                <div class="d-flex align-items-start basic-group gap-1">
                  <div class="summary-icon">
                    <img :src="require('../images/svgs/medals-icon.svg')" alt="Medals Icon">
                  </div>
                  <div>
                    <h6 class="title">{{ customer.translations.medals }}</h6>
                    <h3 class="value">{{ getAllMedalsTotalQuantity() }}</h3>
                    <p class="capacity">
                      {{ customer.translations.maximum_quantity }}
                      (<span>{{ enabledSportsSite === 'Soccer' ? customer.soccer_medals_quota : customer.hockey_medals_quota }}</span>)
                    </p>
                  </div>
                </div>
              </li>
            </ul>
            <!-- Other Products Summary End -->

            <!-- Selected Jerseys Summary -->
            <template v-for="ageGroup in customer.age_groups.flat()">
              <div v-if="isTotalAgeGroupQuantityPresent(ageGroup)" class="table-responsive mb-3">
                Jerseys: {{ ageGroup }}
                <table class="table light-bg m-0 order-table radius-8 summary-table">
                  <thead>
                    <tr>
                      <th>Color</th>
                      <th v-for="variant in getSummaryVariants()" :key="variant.id">{{ variant.size }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="jersey in getSummaryJerseys()">
                      <tr v-if="hasJerseyQuantityInAgeGroup(ageGroup, jersey.id)">
                        <td>
                          <div class="d-flex flex-column gap-1 align-items-center">
                            <img :src="jersey.image_url" class="product-img" width="74" alt="Jersey Image" />
                            <p style="color: #4F2D2CE5; font-size: 11px;">
                              {{ customer.soccer_sports ? jersey.color : jersey.title }}
                            </p>
                          </div>
                        </td>
                        <td v-for="variant in getSummaryVariants()">
                          {{getVariantQuantitySummary(jersey, variant, ageGroup)}}
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </template>

            <div class="d-flex mb-1 gap-2">
              <button v-show="!isMonetaryProgram" href="javascript:void(0)" class="btn outlined-primary-btn px-1 w-50" @click="prevStep">{{ customer.translations.back }}</button>
              <button class="btn primary-btn px-1 w-50" @click="openConfirmOrderModal" :disabled="isCheckoutDisabled">
                {{ customer.translations.checkout }}
              </button>
            </div>

            <button class="text-center btn modal-secondary-btn w-100" @click="openCancelOrderModal">
              {{ customer.translations.cancel_order }}
            </button>
              <!-- Validation Message -->
              <div v-if="isCheckoutDisabled" class="alert alert-warning">
                {{ customer.translations.checkout_message }}
              </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Other Products Section End -->

  <CheckoutPage
    v-if="showCheckoutPage"
    :currentUser="currentUser"
    :customer="customer"
    :vendor="vendor"
    :jerseys="jerseys"
    :variants="variants"
    :orderImage="orderImage"

    :jerseyVariantQuantities="localJerseyVariantQuantities"
    :shortsVariantQuantities="localShortsVariantQuantities"
    :socksVariantQuantities="localSocksVariantQuantities"
    :ballsVariantQuantities="localBallsVariantQuantities"
    :medalsVariantQuantities="localMedalsVariantQuantities"

    :shorts="shorts"
    :shortsVariants="shortsVariants"
    :socks="socks"
    :socksVariants="socksVariants"
    :balls="balls"
    :ballsVariants="ballsVariants"
    :medals="medals"
    :medalsVariants="medalsVariants"

    :hasJerseyQuantityInAgeGroup="hasJerseyQuantityInAgeGroup"
    :isTotalAgeGroupQuantityPresent="isTotalAgeGroupQuantityPresent"
    :enabledSportsSite="enabledSportsSite"
    :addMylarLogoChecked="localAddMylarLogoChecked"
  />

  <CancelOrderModal
    :visible="showCancelOrderModal"
    :currentUser="currentUser"
    :customer="customer"
    :vendor="vendor"
    @close="showCancelOrderModal = false"
  />

  <ConfirmOrderModal
    :jerseys="jerseys"
    :variants="variants"
    :shortsVariants="shortsVariants"
    :socks="socks"
    :socksVariants="socksVariants"
    :ballsVariants="ballsVariants"
    :medalsVariants="medalsVariants"
    :visible="showConfirmOrderModal"
    :currentUser="currentUser"
    :customer="customer"
    :vendor="vendor"
    :orderImage="orderImage"
    :addMylarLogoChecked="localAddMylarLogoChecked"

    :jerseyVariantQuantities="localJerseyVariantQuantities"
    :shortsVariantQuantities="localShortsVariantQuantities"
    :socksVariantQuantities="localSocksVariantQuantities"
    :ballsVariantQuantities="localBallsVariantQuantities"
    :medalsVariantQuantities="localMedalsVariantQuantities"

    @openConfirmAddressModal="showConfirmAddressModal = true"
    @close="showConfirmOrderModal = false"
    :enabledSportsSite="enabledSportsSite"
    :image_present="image_present"

  />

  <ConfirmAddressModal
    :visible="showConfirmAddressModal"
    @close="showConfirmAddressModal = false"
    @openTheCheckoutPage="showCheckoutPage = true"
  />
</template>

<script>
  import SizeGuideModal from './SizeGuideModal.vue';
  import CategoryBullets from './CategoryBullets.vue';
  import InformRmm from './InformRmm.vue';
  import InformRmmModal from './InformRmmModal.vue';
  import LimitExceeded from './LimitExceeded.vue';
  import LimitExceededModal from './LimitExceededModal.vue';
  import CancelOrderModal from './CancelOrderModal.vue';
  import ConfirmOrderModal from './ConfirmOrderModal.vue';
  import CheckoutPage from './CheckoutPage.vue';
  import ConfirmAddressModal from './ConfirmAddressModal.vue';
  import LogoSelection from './LogoSelection.vue';
  import MedalReminderModal from './MedalReminderModal.vue';

  import { driver } from 'driver.js';

  export default {
    components: {
      SizeGuideModal,
      CategoryBullets,
      InformRmm,
      InformRmmModal,
      LimitExceeded,
      LimitExceededModal,
      CancelOrderModal,
      ConfirmOrderModal,
      CheckoutPage,
      ConfirmAddressModal,
      LogoSelection,
      MedalReminderModal
    },
    data() {
      return {
        stepMap: {
          0: 'jerseys',
          1: 'logo selection',
          2: 'logo upload',
          3: 'other products',
          4: 'checkout page'
        },
        localStepIndex: this.currentStepIndex,

        isModalVisible: false,

        showInformRmmAlert: false,
        showInformRmmModal: false,

        showLimitExceededAlert: false,

        showLimitExceededModal: false,
        showInventoryLimitModal: false,

        showCancelOrderModal: false,
        showConfirmOrderModal: false,
        showConfirmAddressModal: false,
        showMedalReminderModal: false,

        selectedAgeGroup: '',
        ageDropdownOpen: false,
        ageGroupSearch: '',
        currentAgeIndex: 0,
        localAgeGroups: this.ageGroups || [],

        activeAgeGroupIndex: 0,

        // Team Breakdown Quantities
        teamBreakdownQuantities: this.initializeTeamBreakdownQuantities(this.customer.age_groups, this.variants),

        localJerseyVariantQuantities: this.jerseyVariantQuantities || this.initializeJerseyVariantQuantities(this.jerseys, this.variants),
        localShortsVariantQuantities: this.shortsVariantQuantities || this.initializeVariantQuantities(this.shorts, this.shortsVariants),
        localSocksVariantQuantities: this.socksVariantQuantities || this.initializeVariantQuantities(this.socks, this.socksVariants),
        localBallsVariantQuantities: this.ballsVariantQuantities || this.initializeVariantQuantities(this.balls, this.ballsVariants),
        localMedalsVariantQuantities: this.medalsVariantQuantities || this.initializeMedalsVariantQuantities(this.medals, this.medalsVariants),

        localAddMylarLogoChecked: false, // Initial Default

        enabledSportsSite: this.enabledSportsSite,

        showCheckoutPage: false,
        isOrderCompleted: this.customer.program_type === 'Monetary' ?  true : false,
        isMonetaryProgram: this.customer.program_type === 'Monetary' ? true : false,

        // Conditionally set max orderables based on enabledSportsSite
        maxOrderableJerseys: this.enabledSportsSite === 'Soccer' ? this.customer.soccer_jerseys_quota : this.customer.hockey_jerseys_quota,
        maxOrderableShorts: this.enabledSportsSite === 'Soccer' ? this.customer.soccer_shorts_quota: null,
        maxOrderableSocks: this.enabledSportsSite === 'Soccer' ? this.customer.soccer_socks_quota : this.customer.hockey_socks_quota,
        maxOrderableBalls: this.enabledSportsSite === 'Soccer' ? this.customer.soccer_balls_quota : null,
        maxOrderableMedals: this.enabledSportsSite === 'Soccer' ? this.customer.soccer_medals_quota : this.customer.hockey_medals_quota,
        
        activeProductTab: 'new',
        activeSocksProductTab: 'new',
        newProducts: null,
        oldProducts: null,

        localTotalJerseysOrdered: this.totalJerseysOrdered || 0,
        localTotalShortsOrdered: this.totalShortsOrdered || 0,
        localTotalSocksOrdered: this.totalSocksOrdered || 0,
        localTotalBallsOrdered: this.totalBallsOrdered || 0,
        localTotalMedalsOrdered: this.totalMedalsOrdered || 0,
      };
    },
    // Default AgeGroup that is selected on page render
    created() {
      if (this.customer && this.customer.age_groups && this.customer.age_groups.length > 0) {
        this.selectedAgeGroup = this.customer.age_groups[0];
      }
    },
    mounted() {
      this.localAgeGroups = this.customer.age_groups.flat();
      this.selectedAgeGroup = this.localAgeGroups[0];
      const medalIds = Object.keys(this.localMedalsVariantQuantities);
      if (medalIds.length > 0) {
        const firstMedalId = medalIds[0];
        const variants = this.localMedalsVariantQuantities[firstMedalId].variants;
        this.localAddMylarLogoChecked = !!variants.addMylarLogo;
      }

      if (this.shouldShowProductTabs && this.oldJerseys && this.newJerseys) {
        this.splitJerseysByProductType(this.jerseys);
      }
    },
    props: {
      currentUser: {
        type: Object,
        required: true
      },
      customer: {
        type: Object,
        required: true
      },
      vendor: {
        type: Object,
        required: true
      },
      rmm: {
        type: Object,
        required: true
      },

      jerseys: {
        type: Object,
        required: true
      },
      oldJerseys: {
        type: Array,
        default: null
      },
      newJerseys: {
        type: Array,
        default: null
      },
      oldVariants: {
        type: Array,
        default: null
      },
      newVariants: {
        type: Array,
        default: null
      },
      variants: {
        type: Object,
        required: true
      },
      shorts: {
        type: Object,
        required: true
      },
      shortsVariants: {
        type: Object,
        required: true
      },
      socks: {
        type: Object,
        required: true
      },
      socksVariants: {
        type: Object,
        required: true
      },
      oldSocks: {
        type: Array,
        default: null
      },
      newSocks: {
        type: Array,
        default: null
      },
      oldSocksVariants: {
        type: Array,
        default: null
      },
      newSocksVariants: {
        type: Array,
        default: null
      },
      balls: {
        type: Object,
        required: true
      },
      ballsVariants: {
        type: Object,
        required: true
      },
      medals: {
        type: Object,
        required: true
      },
      medalsVariants: {
        type: Object,
        required: true
      },

      notifications: {
        type: Array,
        required: true
      },
      enabledSportsSite: {
        type: String,
        required: true
      },

      orderImage: {
        type: File,
        required: false
      },
      currentStepIndex: {
        type: Number,
        default: 0,
        required: false
      },

      ageGroups: {
        type: Array,
        required: true
      },

      totalJerseysOrdered: {
        type: Number,
        required: true
      },
      jerseyVariantQuantities: {
        type: Object,
        required: true
      },

      totalShortsOrdered: {
        type: Number,
        required: true
      },
      shortsVariantQuantities: {
        type: Object,
        required: true
      },

      totalSocksOrdered: {
        type: Number,
        required: true
      },
      socksVariantQuantities: {
        type: Object,
        required: true
      },

      totalBallsOrdered: {
        type: Number,
        required: true
      },
      ballsVariantQuantities: {
        type: Object,
        required: true
      },

      totalMedalsOrdered: {
        type: Number,
        required: true
      },
      medalsVariantQuantities: {
        type: Object,
        required: true
      },
      image_present: {
        type: Boolean,
        required: true
      },
      startGuide: {
        type: Boolean,
        default: false
      },
      show_old_products: {
        type: Boolean,
        required: true
      }
    },
    // Get the AgeGroups for a Cutomer
    // Set AgeGroup that is selected on page render (Seems redundent, See created())
    mounted() {
      this.localAgeGroups = this.customer.age_groups.flat();
      this.selectedAgeGroup = this.localAgeGroups[0];

      // Assuming there is only one medal variant
      const medalIds = Object.keys(this.localMedalsVariantQuantities);
      if (medalIds.length > 0) {
        const firstMedalId = medalIds[0];
        const variants = this.localMedalsVariantQuantities[firstMedalId].variants;

        // Set the checkbox based on the existing value
        this.localAddMylarLogoChecked = !!variants.addMylarLogo;
      }
    },
    computed: {
      filteredAgeGroups() {
        const term = this.ageGroupSearch.trim().toLowerCase();
        if (!term) return this.localAgeGroups;
        return this.localAgeGroups.filter(ageGroup =>
          String(this.customer.translations[ageGroup] || ageGroup).toLowerCase().includes(term)
        );
      },
      visibleAgeGroups() {
        return this.localAgeGroups;
      },
      currentStepName() {
        return this.stepMap[this.localStepIndex];
      },
      isCheckoutDisabled() {
        const totals = [
          this.getAllJerseysTotalQuantity(),
          this.getAllSocksTotalQuantity(),
          this.getAllShortsTotalQuantity(),
          this.getAllBallsTotalQuantity(),
          this.getAllMedalsTotalQuantity()
        ];

        // Disable checkout if all the categories are 0
        return totals.every(total => total === 0);
      },
      sortedShortsVariants() {
        const sizeOrder = ['YXS', 'YS', 'YM', 'YL'];
        return [...this.shortsVariants].sort((a, b) => {
          const aIndex = sizeOrder.indexOf(a.size);
          const bIndex = sizeOrder.indexOf(b.size);
          return aIndex - bIndex;
        });
      },
      shouldShowProductTabs() {
        return this.vendor && this.oldJerseys && this.newJerseys;
      },
      shouldShowSocksProductTabs() {
        return this.vendor && this.oldSocks && this.newSocks && this.enabledSportsSite === 'Hockey';
      },
      displaySocks() {
        if (!this.shouldShowSocksProductTabs) {
          return this.socks;
        }
        if (this.activeSocksProductTab === 'new') {
          const socks = this.newSocks ? this.convertToObject(this.newSocks) : this.socks;
          return socks;
        } else {
          const socks = this.oldSocks ? this.convertToObject(this.oldSocks) : this.socks;
          return socks;
        }
      },
      displaySocksVariants() {
        if (!this.shouldShowSocksProductTabs) {
          return this.socksVariants;
        }
        if (this.activeSocksProductTab === 'new') {
          const variants = this.newSocksVariants || this.socksVariants;
          return variants;
        } else {
          const variants = this.oldSocksVariants || this.socksVariants;
          return variants;
        }
      },
      displayJerseys() {
        if (!this.shouldShowProductTabs) {
          return this.jerseys;
        }
        if (this.activeProductTab === 'new') {
          const jerseys = this.newJerseys ? this.convertToObject(this.newJerseys) : this.jerseys;
          return jerseys;
        } else {
          return this.oldJerseys ? this.convertToObject(this.oldJerseys) : this.jerseys;
        }
      },
      displayVariants() {
        if (!this.shouldShowProductTabs) {
          return this.variants;
        }
        if (this.activeProductTab === 'new') {
          const variants = this.newVariants || this.variants;
          return variants;
        } else {
          const variants = this.oldVariants || this.variants;
          return variants;
        }
      },
      canShowEnglishMedals() {
        return this.rmm?.show_english_medals;
      },
      canShowFrenchMedals() {
        return this.rmm?.show_french_medals;
      }
    },
    watch: {
      activeAgeGroupIndex(newIndex) {
        this.selectedAgeGroup = this.localAgeGroups[newIndex];
      },
      selectedAgeGroup(newAgeGroup) {
        let newIndex = this.localAgeGroups.indexOf(newAgeGroup);
        if (newIndex >= 0) {
          this.activeAgeGroupIndex = Math.min(newIndex, this.localAgeGroups.length);
        }
      },
      startGuide(newVal) {
        if (newVal) {
          this.startGuideTour();
        }
      },
      jerseys(newVal) {
        if (this.shouldShowProductTabs && newVal) {
          this.splitJerseysByProductType(newVal);
        }
      },
      activeProductTab(newTab) {
        if (this.shouldShowProductTabs) {
          this.reinitializeTeamBreakdownQuantities();
        }
      }
    },
    methods: {
      toggleAgeDropdown() {
        this.ageDropdownOpen = !this.ageDropdownOpen;
        this.ageGroupSearch = '';
        if (this.ageDropdownOpen) {
          this.$nextTick(() => this.$refs.ageSearchInput && this.$refs.ageSearchInput.focus());
        }
      },
      selectAgeGroupOption(ageGroup) {
        this.selectedAgeGroup = ageGroup;
        this.ageDropdownOpen = false;
      },
      onAgeDropdownBlur(event) {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          this.ageDropdownOpen = false;
        }
      },
      setActiveProductTab(tab) {
        this.activeProductTab = tab;
      },
      setActiveSocksProductTab(tab) {
        this.activeSocksProductTab = tab;
      },
      
      convertToObject(jerseysArray) {
        const obj = {};
        jerseysArray.forEach(jersey => {
          obj[jersey.id] = jersey;
        });
        return obj;
      },
      
      reinitializeTeamBreakdownQuantities() {
        const currentVariants = this.displayVariants;
        const newQuantities = {};
        
        if (this.customer && this.customer.age_groups) {
          this.customer.age_groups.flat().forEach((ageGroup) => {
            newQuantities[ageGroup] = {};
            currentVariants.forEach(variant => {
              newQuantities[ageGroup][variant.size] = 0;
            });
          });
        }
        
        this.teamBreakdownQuantities = newQuantities;
      },
      initializeJerseyVariantQuantities(products, variants) {
        let quantities = {};

        if (!products || !this.customer || !this.customer.age_groups) return quantities;

        this.customer.age_groups.flat().forEach((ageGroup) => {
          quantities[ageGroup] = {};

          products.forEach((product) => {
            quantities[ageGroup][product.id] = {
              variants: {}
            };

            product.variants.forEach((variant) => {
              quantities[ageGroup][product.id].variants[variant.id] = null;
            });
          });
        });

        return quantities;
      },

      teamBreakdownTotal(ageGroup) {
        if (!this.teamBreakdownQuantities || !this.teamBreakdownQuantities[ageGroup]) return 0;
        return Object.values(this.teamBreakdownQuantities[ageGroup]).reduce((sum, v) => {
          return sum + (Number(v) || 0);
        }, 0);
      },

      initializeVariantQuantities(products, variants) {
        let quantities = {};

        if (!products || !this.customer) return quantities;

        products.forEach((product) => {
          quantities[product.id] = {
            variants: {}
          };

          product.variants.forEach((variant) => {
            quantities[product.id].variants[variant.id] = 0;
          });
        });

        return quantities;
      },

      initializeTeamBreakdownQuantities(ageGroups, variants) {
        let quantities = {};
        if (!ageGroups || !variants) return quantities;

        ageGroups.flat().forEach((ageGroup) => {
          quantities[ageGroup] = {};
          variants.forEach((variant) => {
            quantities[ageGroup][variant.size] = null;
          });
        });

        return quantities;
      },

      initializeMedalsVariantQuantities(products, variants) {
        let quantities = {};

        if (!products || !this.customer) return quantities;

        products.forEach((product) => {
          quantities[product.id] = {
            variants: {}
          };

          variants.forEach((variant) => {
            quantities[product.id].variants[variant.id] = null;
            quantities[product.id].variants['addMylarLogo'] = false;
          });
        });

        return quantities;
      },

      matchingJerseyVariant(jersey, needed_variant_size) {
        return jersey.variants.find(jersey_variant => jersey_variant.size === needed_variant_size) || null;
      },
      matchingShortVariant(short, needed_variant_size) {
        return short.variants.find(short_variant => short_variant.size === needed_variant_size) || null;
      },
      matchingSocksVariant(sock, needed_variant_size) {
        return sock.variants.find(sock_variant => sock_variant.size === needed_variant_size) || null;
      },
      matchingBallsVariant(ball, needed_variant_size) {
        return ball.variants.find(ball_variant => ball_variant.size === needed_variant_size) || null;
      },
      matchingMedalsVariant(medal, needed_variant_size) {
        return medal.variants.find(medal_variant => medal_variant.size === needed_variant_size) || null;
      },

      validateTeamBreakdownInput(size) {
        // Ensure the input is a non-negative number
        if (this.teamBreakdownQuantities[this.selectedAgeGroup][size] < 0) {
          this.teamBreakdownQuantities[this.selectedAgeGroup][size] = 0;
        }
      },

      variantInputId(product, variant) {
        return `${product.id}_${variant.id}`;
      },

      hasInventory(variant) {
        return variant.quantity > 0;
      },

      getInventory(variantId, jerseyId) {
        if (!jerseyId) {
          console.warn("Jersey ID is NULL, something went wrong.");
          return 0;
        }

        const displayJerseys = this.displayJerseys;
        let jersey = displayJerseys[jerseyId] || Object.values(displayJerseys).find(j => j.id == jerseyId);
        if (!jersey) {
          console.warn("Jersey not found for ID:", jerseyId);
          return 0;
        }

        let variant = jersey.variants.find(v => v.id === variantId);
        if (!variant) {
          console.warn("Variant not found in Jersey ID:", jerseyId, "for Variant ID:", variantId);
          return 0;
        }

        return variant.quantity;
      },

      getShortsInventory(variantId) {
        let variant = this.shortsVariants.find(v => v.id == variantId);
        return variant ? variant.quantity : 0;
      },

      getSocksInventory(sockId, variantId) {
        let sock_pair = this.findSockForAutofill(sockId);
        let variant = sock_pair?.variants?.find(v => v.id == variantId);

        return variant?.quantity || 0;
      },

      getBallsInventory(variantId) {
        let variant = this.ballsVariants.find(v => v.id == variantId);
        return variant ? variant.quantity : 0;
      },

      getMedalsInventory(variantId) {
        let variant = this.medalsVariants.find(v => v.id == variantId);
        return variant ? variant.quantity : 0;
      },

      updateMylarLogo(event) {
        const isChecked = event.target.checked;
        this.localAddMylarLogoChecked = isChecked;

        Object.keys(this.localMedalsVariantQuantities).forEach((medalId) => {
          let variants = this.localMedalsVariantQuantities[medalId].variants;
          variants.addMylarLogo = isChecked;
        });

        console.log('Checkbox checked:', isChecked);
      },

      updateSelectedAgeGroup(ageGroup) {
        this.selectedAgeGroup = ageGroup;
      },

      isTotalAgeGroupQuantityPresent(ageGroup) {
        const ageGroupData = this.localJerseyVariantQuantities[ageGroup];
        if (!ageGroupData) return false;

        let total = 0;

        Object.keys(ageGroupData).forEach((jerseyId) => {
          const variants = ageGroupData[jerseyId]?.variants || {};
          total += Object.values(variants).reduce((sum, qty) => sum + (parseInt(qty, 10) || 0), 0);
        });

        return total > 0;
      },

      getJerseyTotalQuantity(jerseyId) {
        let productQuantities = this.localJerseyVariantQuantities[this.selectedAgeGroup]?.[jerseyId];

        if (!productQuantities) return 0;

        return Object.values(productQuantities.variants).reduce((sum, qty) => {
          return sum + (isNaN(parseInt(qty)) ? 0 : parseInt(qty));
        }, 0);
      },
      getJerseyVariantTotalQuantity(jerseyId, variantId) {
        let total = 0;

        Object.keys(this.localJerseyVariantQuantities).forEach((ageGroup) => {
          let productQuantities = this.localJerseyVariantQuantities[ageGroup][jerseyId].variants[variantId];

          if (productQuantities) {
            total += productQuantities
          }
        });

        return total;
      },

      getExcludedJerseyVariantTotalQuantity(excludeVariantId) {
        let total = 0;
        let selectedAgeGroup = this.selectedAgeGroup;

        // Loop through all age groups
        Object.keys(this.localJerseyVariantQuantities).forEach((ageGroup) => {
          Object.keys(this.localJerseyVariantQuantities[ageGroup]).forEach((jerseyId) => {
            // Loop through all variants of each jersey
            Object.keys(this.localJerseyVariantQuantities[ageGroup][jerseyId].variants).forEach((variantId) => {
              let productQuantities = this.localJerseyVariantQuantities[ageGroup][jerseyId].variants[variantId];

              // If it's the selected age group, exclude the variant
              if (ageGroup == selectedAgeGroup) {
                if (variantId != excludeVariantId) {
                  if (productQuantities) {
                    total += productQuantities;
                  }
                }
              } else {
                // For all other age groups, include all variants
                if (productQuantities) {
                  total += productQuantities;
                }
              }
            });
          });
        });

        return total;
      },

      getExcludedShortVariantTotalQuantity(excludeVariantId) {
        let total = 0;

        // Loop through all products in localShortsVariantQuantities
        Object.keys(this.localShortsVariantQuantities).forEach((productId) => {
          let productVariants = this.localShortsVariantQuantities[productId].variants;

          // Loop through each variant in the product
          Object.keys(productVariants).forEach((variantId) => {ss
            let variantQuantity = productVariants[variantId];

            // Add to total if it's not the excluded variant ID
            if (variantId != excludeVariantId) {
              total += variantQuantity || 0; // Treat null/undefined as 0
            }
          });
        });

        return total;
      },

      getExcludedSocksVariantTotalQuantity(excludeVariantId) {
        let total = 0;

        // Loop through all products in localShortsVariantQuantities
        Object.keys(this.localSocksVariantQuantities).forEach((productId) => {
          let productVariants = this.localSocksVariantQuantities[productId].variants;

          // Loop through each variant in the product
          Object.keys(productVariants).forEach((variantId) => {
            let variantQuantity = productVariants[variantId];

            // Add to total if it's not the excluded variant ID
            if (variantId != excludeVariantId) {
              total += variantQuantity || 0; // Treat null/undefined as 0
            }
          });
        });

        return total;
      },

      getAllJerseysTotalQuantity() {
        let total = 0;

        Object.keys(this.localJerseyVariantQuantities).forEach((ageGroup) => {
          Object.keys(this.localJerseyVariantQuantities[ageGroup]).forEach((jerseyId) => {
            let jerseyQuantities = this.localJerseyVariantQuantities[ageGroup][jerseyId];

            if (jerseyQuantities && jerseyQuantities.variants) {
              total += Object.values(jerseyQuantities.variants).reduce((sum, qty) => {
                return sum + (Number(qty) || 0);
              }, 0);
            }
          });
        });

        return total;
      },

      getMatchingJerseyTotal(sock) {
        const sockName = sock.name || sock.title;
        const sockTags = sock.tags || [];

        const isOldProduct = sockTags.includes('old_product');
        const isNewProduct = sockTags.includes('new_product');

        const allJerseys = [...(this.oldJerseys || []), ...(this.newJerseys || [])];
        const matchingJersey = allJerseys.find(jersey => {
          if ((jersey.name || jersey.title) !== sockName) return false;

          const jerseyTags = jersey.tags || [];
          if (isOldProduct && !jerseyTags.includes('old_product')) return false;
          if (isNewProduct && !jerseyTags.includes('new_product')) return false;

          return true;
        });

        if (!matchingJersey) return 0;

        const matchingJerseyId = String(matchingJersey.id);
        let total = 0;

        Object.keys(this.localJerseyVariantQuantities).forEach((ageGroup) => {
          Object.keys(this.localJerseyVariantQuantities[ageGroup]).forEach((jerseyId) => {
            if (jerseyId !== matchingJerseyId) return;

            const jerseyQuantities = this.localJerseyVariantQuantities[ageGroup][jerseyId];

            if (jerseyQuantities && jerseyQuantities.variants) {
              total += Object.values(jerseyQuantities.variants).reduce((sum, qty) => {
                return sum + (Number(qty) || 0);
              }, 0);
            }
          });
        });

        return total;
      },

      getShortTotalQuantity(shortId) {
        let total = 0;

        if (this.localShortsVariantQuantities[shortId]) {
          total += Object.values(this.localShortsVariantQuantities[shortId].variants).reduce((sum, qty) => {
            return sum + (parseInt(qty, 10) || 0);
          }, 0);
        }

        return total;
      },
      getAllShortsTotalQuantity() {
        let total = 0;
        let shortIds = new Set();

        let shorts = Object.keys(this.localShortsVariantQuantities);

        shorts.forEach((shortId) => {
          if (!shortIds.has(shortId)) {
            shortIds.add(shortId);

            total += Object.values(this.localShortsVariantQuantities[shortId].variants).reduce((sum, qty) => {
              return sum + (parseInt(qty, 10) || 0);
            }, 0);
          }
        });

        return total;
      },

      getSockTotalQuantity(sockId) {
        let total = 0;

        if (this.localSocksVariantQuantities[sockId]) {
          total += Object.values(this.localSocksVariantQuantities[sockId].variants).reduce((sum, qty) => {
            return sum + (parseInt(qty, 10) || 0);
          }, 0);
        }

        return total;
      },
      getAllSocksTotalQuantity() {
        let total = 0;
        let sockIds = new Set();

        let socks = Object.keys(this.localSocksVariantQuantities);

        socks.forEach((sockId) => {
          if (!sockIds.has(sockId)) {
            sockIds.add(sockId);

            total += Object.values(this.localSocksVariantQuantities[sockId].variants).reduce((sum, qty) => {
              return sum + (parseInt(qty, 10) || 0);
            }, 0);
          }
        });

        return total;
      },

      getBallTotalQuantity(ballId) {
        let total = 0;

        if (this.localBallsVariantQuantities[ballId]) {
          total += Object.values(this.localBallsVariantQuantities[ballId].variants).reduce((sum, qty) => {
            return sum + (parseInt(qty, 10) || 0);
          }, 0);
        }

        return total;
      },
      getAllBallsTotalQuantity() {
        let total = 0;
        let ballIds = new Set();

        let balls = Object.keys(this.localBallsVariantQuantities);

        balls.forEach((ballId) => {
          if (!ballIds.has(ballId)) {
            ballIds.add(ballId);

            total += Object.values(this.localBallsVariantQuantities[ballId].variants).reduce((sum, qty) => {
              return sum + (parseInt(qty, 10) || 0);
            }, 0);
          }
        });

        return total;
      },

      getMedalTotalQuantity(medalId, medalVariantId = null) {
        let total = 0;

        if (medalVariantId != null) {
          return this.localMedalsVariantQuantities[medalId].variants[medalVariantId];
        }

        if (this.localMedalsVariantQuantities[medalId]) {
          total += Object.values(this.localMedalsVariantQuantities[medalId].variants).reduce((sum, qty) => {
            return sum + (parseInt(qty, 10) || 0);
          }, 0);
        }

        return total;
      },
      getAllMedalsTotalQuantity() {
        let total = 0;
        let medalIds = new Set();

        let medals = Object.keys(this.localMedalsVariantQuantities);

        medals.forEach((medalId) => {
          if (!medalIds.has(medalId)) {
            medalIds.add(medalId);

            total += Object.values(this.localMedalsVariantQuantities[medalId].variants).reduce((sum, qty) => {
              return sum + (parseInt(qty, 10) || 0);
            }, 0);
          }
        });

        return total;
      },

      checkValidations(product_id, variant_id, product_type = null) {
        if (product_type === 'Jerseys') {
          let totalOrderedAcrossGroups = this.getAllJerseysTotalQuantity();

          // Get total ordered for the current jersey in the current age-group/division
          let totalOrderedForJersey = this.getJerseyVariantTotalQuantity(product_id, variant_id);

          let totalOrderedForJerseyOtherThenCurrentVariant = this.getExcludedJerseyVariantTotalQuantity(variant_id);

          let inventory = this.getInventory(variant_id, product_id);

          let remainingOrderable = this.maxOrderableJerseys - totalOrderedForJerseyOtherThenCurrentVariant;

          var allowedJerseys = totalOrderedAcrossGroups;

          // Always update total
          this.localTotalJerseysOrdered = totalOrderedAcrossGroups;

          // Validate against inventory and remaining orderable quantity
          if (remainingOrderable == 0) {
            this.showInformRmmModal = true;

            $("#" + product_id + "_" + variant_id).val(0);

            this.localJerseyVariantQuantities[this.selectedAgeGroup][product_id].variants[variant_id] = 0;

            allowedJerseys = 0;

            return;
          }

          if (totalOrderedAcrossGroups > this.maxOrderableJerseys) {
            this.showInformRmmModal = true;

            if (remainingOrderable <= inventory) {
              let setInventory = this.maxOrderableJerseys - totalOrderedForJerseyOtherThenCurrentVariant;

              $("#" + product_id + "_" + variant_id).val(setInventory);

              this.localJerseyVariantQuantities[this.selectedAgeGroup][product_id].variants[variant_id] = setInventory;

              allowedJerseys = setInventory;

              this.localTotalJerseysOrdered = totalOrderedForJerseyOtherThenCurrentVariant + setInventory;
            }
          }
        }

        else if (product_type == 'Shorts') {
          const totalOrdered = this.getAllShortsTotalQuantity();
          const remainingOrderable = this.maxOrderableShorts - this.getExcludedShortVariantTotalQuantity(variant_id);
          if (remainingOrderable == 0) {
            $("#" + product_id + "_" + variant_id).val(0);
            this.localShortsVariantQuantities[product_id].variants[variant_id] = 0;
            return;
          }

          if (totalOrdered > this.maxOrderableShorts) {
            this.showInformRmmModal = true;

            if (remainingOrderable <= this.getShortsInventory(variant_id)) {

              let setInventory = this.maxOrderableJerseys - this.getExcludedShortVariantTotalQuantity(variant_id);
              $("#" + product_id + "_" + variant_id).val(setInventory);
              this.localShortsVariantQuantities[product_id].variants[variant_id] = setInventory;
            }
          }
        }

        else if (product_type == 'Socks') {
          const totalOrdered = this.getAllSocksTotalQuantity();

          // Show modal if quota is 0 or null (no quota available)
          if (this.maxOrderableSocks == null || this.maxOrderableSocks === 0) {
            this.showInformRmmModal = true;
            $("#" + product_id + "_" + variant_id).val(0);
            this.localSocksVariantQuantities[product_id].variants[variant_id] = 0;
            return;
          }

          const remainingOrderable = this.maxOrderableSocks - this.getExcludedSocksVariantTotalQuantity(variant_id);

          if (remainingOrderable == 0) {
            $("#" + product_id + "_" + variant_id).val(0);
            this.localSocksVariantQuantities[product_id].variants[variant_id] = 0;
            return;
          }

          if (totalOrdered > this.maxOrderableSocks) {
            this.showInformRmmModal = true;

            if (remainingOrderable <= this.getSocksInventory(product_id, variant_id)) {

              let setInventory = this.maxOrderableSocks - this.getExcludedSocksVariantTotalQuantity(variant_id);
              $("#" + product_id + "_" + variant_id).val(setInventory);
              this.localSocksVariantQuantities[product_id].variants[variant_id] = setInventory;
            }
          }

        }

        else if (product_type == 'Balls') {
          if (this.getAllBallsTotalQuantity() > this.maxOrderableBalls) {
            this.showInformRmmAlert = true;
            $("#" + product_id + "_" + variant_id).val(this.maxOrderableBalls);
            this.localBallsVariantQuantities[product_id].variants[variant_id] = this.maxOrderableBalls;
          }
        }

        else if (product_type == 'Medals') {
          let totalOrdered = this.getMedalTotalQuantity(product_id);
          let inventory = this.getMedalsInventory(variant_id);

          // Show modal if quota is 0 or null (no quota available)
          if (this.maxOrderableMedals == null || this.maxOrderableMedals === 0) {
            this.showInformRmmModal = true;
            $("#" + product_id + "_" + variant_id).val(0);
            this.localMedalsVariantQuantities[product_id].variants[variant_id] = 0;
            return;
          }

          if (totalOrdered > inventory) {
            // this.showLimitExceededAlert = true;
            this.showLimitExceededModal = true;
            $("#" + product_id + "_" + variant_id).val(inventory);
            this.localMedalsVariantQuantities[this.selectedAgeGroup][product_id][variant_id] = inventory;
          }
          else if (this.getAllMedalsTotalQuantity() > this.maxOrderableMedals) {
            // this.showInformRmmAlert = true;
            this.showInformRmmModal = true;
            $("#" + product_id + "_" + variant_id).val(this.maxOrderableMedals);
            this.localMedalsVariantQuantities[product_id].variants[variant_id] = this.maxOrderableMedals;
          }
        }
      },

      validateInput(itemType, itemId, variantId, maxQuantity) {
        let currentValue;

        switch (itemType) {
          case 'jerseys':
            currentValue = this.localJerseyVariantQuantities[this.selectedAgeGroup][itemId].variants[variantId];
            if (currentValue > maxQuantity) {
              this.localJerseyVariantQuantities[this.selectedAgeGroup][itemId].variants[variantId] = maxQuantity;
            } else if (currentValue < 0) {
              this.localJerseyVariantQuantities[this.selectedAgeGroup][itemId].variants[variantId] = 0;
            }
            break;

          case 'shorts':
            currentValue = this.localShortsVariantQuantities[itemId].variants[variantId];
            if (currentValue > maxQuantity) {
              this.localShortsVariantQuantities[itemId].variants[variantId] = maxQuantity;
            } else if (currentValue < 0) {
              this.localShortsVariantQuantities[itemId].variants[variantId] = 0;
            }
            break;

          case 'socks': // New case for socks
            currentValue = this.localSocksVariantQuantities[itemId].variants[variantId];

            if (currentValue > maxQuantity) {
              this.localSocksVariantQuantities[itemId].variants[variantId] = maxQuantity;
            } else if (currentValue < 0) {
              this.localSocksVariantQuantities[itemId].variants[variantId] = 0;
            }
            break;

          case 'balls': // New case for socks
            currentValue = this.localBallsVariantQuantities[itemId].variants[variantId];
            if (currentValue > maxQuantity) {
              this.localBallsVariantQuantities[itemId].variants[variantId] = maxQuantity;
            } else if (currentValue < 0) {
              this.localBallsVariantQuantities[itemId].variants[variantId] = 0;
            }
            break;

          case 'medals': // New case for socks
            currentValue = this.localMedalsVariantQuantities[itemId].variants[variantId];
            if (currentValue > maxQuantity) {
              this.localMedalsVariantQuantities[itemId].variants[variantId] = maxQuantity;
            } else if (currentValue < 0) {
              this.localMedalsVariantQuantities[itemId].variants[variantId] = 0;
            }
            break;

          default:
            console.error(`Unknown item type: ${itemType}`);
        }
      },

      nextAgeGroup() {
        // Check if there is a next age group
        if (this.currentAgeIndex < this.customer.age_groups.length - 1) {
          this.currentAgeIndex++;
        } else {
          this.currentAgeIndex = 0;
        }

        this.selectedAgeGroup = this.customer.age_groups[this.currentAgeIndex][0];

        // this.updateSelectedAgeGroup(this.selectedAgeGroup);
      },

      hasJerseyQuantityInAgeGroup(ageGroup, jerseyId) {
        const jerseyData = this.localJerseyVariantQuantities[ageGroup]?.[jerseyId];
        if (!jerseyData) return false; // If the jersey doesn't exist, return false

        return Object.values(jerseyData.variants || {}).some((quantity) => quantity > 0);
      },

      decrementJerseyQuantity(jerseyId, variantId) {
        let currentQuantity = this.localJerseyVariantQuantities[this.selectedAgeGroup][jerseyId].variants[variantId];

        if (currentQuantity > 0) {
          this.localJerseyVariantQuantities[this.selectedAgeGroup][jerseyId].variants[variantId] = currentQuantity - 1;
        }
      },

      incrementJerseyQuantity(jerseyId, variantId) {
        let currentQuantity = this.localJerseyVariantQuantities[this.selectedAgeGroup][jerseyId].variants[variantId];

        if (currentQuantity < this.getInventory(variantId, jerseyId)) {
          this.localJerseyVariantQuantities[this.selectedAgeGroup][jerseyId].variants[variantId] = currentQuantity + 1;

          this.checkValidations(jerseyId, variantId, 'Jerseys');
        }
      },

      incrementTeamBreakdownQuantity(size) {
        let currentQty = this.teamBreakdownQuantities[this.selectedAgeGroup][size] || 0;
        this.teamBreakdownQuantities[this.selectedAgeGroup][size] = currentQty + 1;
      },

      decrementTeamBreakdownQuantity(size) {
        let currentQty = this.teamBreakdownQuantities[this.selectedAgeGroup][size] || 0;
        if (currentQty > 0) {
          this.teamBreakdownQuantities[this.selectedAgeGroup][size] = currentQty - 1;
        } else {
          this.teamBreakdownQuantities[this.selectedAgeGroup][size] = 0;
        }
      },


      decrementShortQuantity(shortId, variantId) {
        let currentQuantity = this.localShortsVariantQuantities[shortId].variants[variantId];

        if (currentQuantity > 0) {
          this.localShortsVariantQuantities[shortId].variants[variantId] = currentQuantity - 1;
        }
      },

      incrementShortQuantity(shortId, variantId) {
        let currentQuantity = this.localShortsVariantQuantities[shortId].variants[variantId];

        if (currentQuantity < this.getShortsInventory(variantId)) {
          this.localShortsVariantQuantities[shortId].variants[variantId] = currentQuantity + 1;

          this.checkValidations(shortId, variantId, 'Shorts');
        }
      },

      decrementSockQuantity(sockId, variantId) {
        let currentQuantity = this.localSocksVariantQuantities[sockId].variants[variantId];

        if (currentQuantity > 0) {
          this.localSocksVariantQuantities[sockId].variants[variantId] = currentQuantity - 1;
        }
      },

      incrementSockQuantity(sockId, variantId) {
        let currentQuantity = this.localSocksVariantQuantities[sockId].variants[variantId];

        if (currentQuantity < this.getSocksInventory(sockId, variantId)) {
          this.localSocksVariantQuantities[sockId].variants[variantId] = currentQuantity + 1;

          this.checkValidations(sockId, variantId, 'Socks');
        }
      },

      decrementBallQuantity(ballId, variantId) {
        let currentQuantity = this.localBallsVariantQuantities[ballId].variants[variantId];

        if (currentQuantity > 0) {
          this.localBallsVariantQuantities[ballId].variants[variantId] = currentQuantity - 1;
        }
      },

      incrementBallQuantity(ballId, variantId) {
        let currentQuantity = this.localBallsVariantQuantities[ballId].variants[variantId];

        if (currentQuantity < this.getBallsInventory(variantId)) {
          this.localBallsVariantQuantities[ballId].variants[variantId] = currentQuantity + 1;

          this.checkValidations(ballId, variantId, 'Balls');
        }
      },

      decrementMedalQuantity(medalId, variantId) {
        let currentQuantity = this.localMedalsVariantQuantities[medalId].variants[variantId];

        if (currentQuantity > 0) {
          this.localMedalsVariantQuantities[medalId].variants[variantId] = currentQuantity - 1;
        }
      },

      incrementMedalQuantity(medalId, variantId) {
        let currentQuantity = this.localMedalsVariantQuantities[medalId].variants[variantId];

        if (currentQuantity < this.getMedalsInventory(variantId)) {
          this.localMedalsVariantQuantities[medalId].variants[variantId] = currentQuantity + 1;

          this.checkValidations(medalId, variantId, 'Medals');
        }
      },

      copyJerseyQuantitiesFromAbove(currentIndex) {
        const displayJerseys = this.displayJerseys;
        let currentJersey;
        if (Array.isArray(displayJerseys)) {
          currentJersey = displayJerseys[currentIndex];
        } else {
          const currentJerseyKey = currentIndex.toString();
          currentJersey = displayJerseys[currentJerseyKey];
        }
        if (!currentJersey) {
          return;
        }
        const teamBreakdownForAgeGroup = this.teamBreakdownQuantities[this.selectedAgeGroup];

        if (!teamBreakdownForAgeGroup) {
          return;
        }

        const currentVariants = currentJersey.variants;
        
        if (!currentVariants || !Array.isArray(currentVariants)) {
          return;
        }

        currentVariants.forEach(variant => {
          const variantSize = variant.size;
          const currentVariantId = variant.id;

          const teamBreakdownQty = teamBreakdownForAgeGroup[variantSize];

          if (typeof teamBreakdownQty !== 'number' || teamBreakdownQty <= 0) {
            return;
          }

          const jerseyId = currentJersey.id;
          const totalOrderedForJerseyOtherVariants = this.getExcludedJerseyVariantTotalQuantity(currentVariantId);
          const inventory = this.getInventory(currentVariantId, jerseyId);

          let cappedQty;
          let inventoryWasLimiting = false;
          let quotaWasLimiting = false;

          if (teamBreakdownQty > inventory) {
            cappedQty = inventory;
            inventoryWasLimiting = true;
          } else {
            cappedQty = teamBreakdownQty;
          }

          if (totalOrderedForJerseyOtherVariants + cappedQty > this.maxOrderableJerseys) {
            cappedQty = Math.max(0, this.maxOrderableJerseys - totalOrderedForJerseyOtherVariants);
            quotaWasLimiting = true;
          }

          this.localJerseyVariantQuantities[this.selectedAgeGroup][jerseyId].variants[currentVariantId] = cappedQty;

          if (inventoryWasLimiting) {
              this.showInventoryLimitModal = true;
          } else if (quotaWasLimiting) {
              this.showLimitExceededModal = true; // This is your existing quota modal
          }

          // Update total count
          this.localTotalJerseysOrdered = this.getAllJerseysTotalQuantity();
        });
      },

      getTotalJerseysForAutofill() {
        return this.getAllJerseysTotalQuantity() || this.localTotalJerseysOrdered || 0;
      },

      autoFillSocks(sockId) {
        const sock = this.findSockForAutofill(sockId);
        if (!sock) {
          return;
        }
        const productQuantity = this.getMatchingJerseyTotal(sock);

        const variant = (sock.variants || []).find(v =>
          this.hasInventory(v)
        );

        if (!variant) return;

        if (!this.localSocksVariantQuantities[sock.id]) {
          this.$set(this.localSocksVariantQuantities, sock.id, { variants: {} });
        }
        if (this.localSocksVariantQuantities[sock.id].variants[variant.id] === undefined) {
          this.$set(this.localSocksVariantQuantities[sock.id].variants, variant.id, 0);
        }

        const quantity = Math.min(productQuantity, variant.quantity);
        this.localSocksVariantQuantities[sock.id].variants[variant.id] = quantity;
        this.checkValidations(sock.id, variant.id, 'Socks');
      },

      findSockForAutofill(sockId) {
        const id = String(sockId);
        const matches = (sock) => sock && String(sock.id) === id;

        const fromDisplay = Object.values(this.displaySocks || {}).find(matches);
        if (fromDisplay) return fromDisplay;

        const collections = [this.newSocks, this.oldSocks, this.socks];
        for (const collection of collections) {
          if (!collection) continue;
          const list = Array.isArray(collection) ? collection : Object.values(collection);
          const found = list.find(matches);
          if (found) return found;
        }

        return null;
      },

      autoFillBalls() {
        const totalJerseys = this.getTotalJerseysForAutofill();

        this.balls.forEach(ball => {
          const variants = ball.variants;

          if (variants.length === 1) {
            const variant = variants[0];

            if (this.hasInventory(variant)) {
              const quantity = Math.min(
                totalJerseys,
                variant.quantity
              );

              if (
                this.localBallsVariantQuantities?.[ball.id]?.variants?.[variant.id] !== undefined
              ) {
                this.localBallsVariantQuantities[ball.id].variants[variant.id] = quantity;
                this.checkValidations(ball.id, variant.id, 'Balls');
              }
            }
          }
        });
      },

      autoFillMedals(size = null) {
        const totalJerseys = this.getTotalJerseysForAutofill();

        this.medals.forEach(medal => {
          const variant = this.medalsVariants.find(v =>
            this.matchingMedalsVariant(medal, v.size) &&
            this.hasInventory(v) &&
            (size ? v.size === size : true) &&
            ((v.size === 'ENGLISH' && this.canShowEnglishMedals) ||
             (v.size === 'FRENCH' && this.canShowFrenchMedals))
          );

          if (!variant) return;

          const quantity = Math.min(
            totalJerseys,
            variant.quantity
          );
          this.localMedalsVariantQuantities[medal.id].variants[variant.id] = quantity;
          this.checkValidations(medal.id, variant.id, 'Medals');
        });
      },

      nextStep() {
        let next = this.localStepIndex + 1;
        // Skip logo steps if monetary program
        if (this.isMonetaryProgram || this.enabledSportsSite === 'Soccer' || this.enabledSportsSite === 'Hockey') {
          if (this.localStepIndex === 0) next = 3;
        }

        if (next <= 4 && this.isTransitionValid(this.localStepIndex, next)) {
          this.localStepIndex = next;
        }

        // Show checkout if stepping into it
        if (next === 4) {
          this.showCheckoutPage = true;
          this.isOrderCompleted = true;
        } else {
          this.showCheckoutPage = false;
          this.isOrderCompleted = false;
        }
      },
      prevStep() {
        let prev = this.localStepIndex - 1;

        // Skip logo steps in reverse if monetary program
        if (this.isMonetaryProgram || this.enabledSportsSite === 'Soccer' || this.enabledSportsSite === 'Hockey') {
          if (this.localStepIndex === 3) prev = 0; // Other Products → Jerseys
        }

        // If we're on step 3 and trying to go to step 2 is not valid, force go to step 1
        if (this.localStepIndex === 3 && !this.isTransitionValid(this.localStepIndex, prev)) {
          prev = 1; // Force to go to step 1 (Logo Selection)
        }

        if (prev >= 0 && this.isTransitionValid(this.localStepIndex, prev)) {
          this.localStepIndex = prev;
        }

        // Leaving checkout step
        if (prev < 4) {
          this.showCheckoutPage = false;
          this.isOrderCompleted = false;
        }
      },
      isTransitionValid(from, to) {
        if (this.isMonetaryProgram || this.enabledSportsSite === 'Soccer' || this.enabledSportsSite === 'Hockey') {
          const validTransitions = {
            0: [3],
            3: [0, 4],
            4: []
          };

          return validTransitions[from]?.includes(to);
        }

        // Full flow with logos
        const validTransitions = {
          0: [1],        // Jerseys → Logo Selection
          1: [0, 2, 3],  // Logo Selection → Jerseys OR Logo Upload OR Other Products
          2: [1, 3],     // Logo Upload → Logo Selection OR Other Products
          3: [1, 4],     // Other Products → Logo Selection OR Checkout
          4: []          // Checkout = terminal step
        };

        return validTransitions[from]?.includes(to);
      },

      openModal() {
        this.isModalVisible = true;
      },
      openCancelOrderModal() {
        this.showCancelOrderModal = true;
      },
      openConfirmOrderModal() {
        const medalsTotal = this.getAllMedalsTotalQuantity();
        if (medalsTotal === 0) {
          this.showMedalReminderModal = true;
        } else {
          this.showConfirmOrderModal = true;
        }
      },
      openConfirmAddressModal() {
        this.showConfirmAddressModal = true;
      },
      scrollToMedals() {
        setTimeout(() => {
          const medalsSection = document.querySelector('[id*="medals"]');
          if (medalsSection) {
            medalsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            // Try alternative selectors
            const medalsCard = document.querySelector('.medals-section') || 
                              document.querySelector('#medals') ||
                              document.querySelector('[class*="medal"]');
            if (medalsCard) {
              medalsCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }, 100);
      },

      getVariantQuantitySummary(jersey, variant, ageGroup) {
        const quantities = this.localJerseyVariantQuantities[ageGroup]?.[jersey.id]?.variants;
        let quantity = quantities?.[variant.id];
        
        if (typeof quantity === 'undefined' && quantities) {     
          const matchingStoredId = Object.keys(quantities).find(storedVariantId => {
            const storedVariant = jersey.variants.find(v => v.id == storedVariantId);
            return storedVariant && storedVariant.size === variant.size;
          });
          if (matchingStoredId) {
            quantity = quantities[matchingStoredId];
          }
        }
        const result = quantity !== null && quantity !== undefined ? quantity : '-';
        return result;
      },
      getFirstJerseyId() {
        if (!this.displayJerseys) return null;
        const jerseys = Object.values(this.displayJerseys);
        return jerseys.length > 0 ? jerseys[0].id : null;
      },
      getSummaryVariants() {
        if (this.vendor && this.vendor.name === 'Creo Marketing' && this.enabledSportsSite === 'Soccer') {
          const sizeOrder = ['4T', '5T', '6T', 'YXS', 'YS', 'YM', 'YL', 'YXL'];
          const uniqueVariantsMap = new Map();
          
          if (this.newVariants) {
            this.newVariants.forEach(variant => {
              if (!uniqueVariantsMap.has(variant.size)) {
                uniqueVariantsMap.set(variant.size, variant);
              }
            });
          }
          
          if (this.oldVariants) {
            this.oldVariants.forEach(variant => {
              if (!uniqueVariantsMap.has(variant.size)) {
                uniqueVariantsMap.set(variant.size, variant);
              }
            });
          }
          
          if (uniqueVariantsMap.size === 0) {
            return this.variants || [];
          }
          const uniqueVariants = Array.from(uniqueVariantsMap.values());
          uniqueVariants.sort((a, b) => {
            const aIndex = sizeOrder.indexOf(a.size);
            const bIndex = sizeOrder.indexOf(b.size);
            
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex;
            }
            
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            
            return a.size.localeCompare(b.size);
          });
          
          return uniqueVariants;
        }
        
        return this.displayVariants || [];
      },
      getSummaryJerseys() {
        if (this.vendor && this.vendor.name === 'Creo Marketing' && (this.enabledSportsSite === 'Soccer' || this.enabledSportsSite === 'Hockey')) {
          const allJerseys = {};
          
          if (this.newJerseys) {
            const newJerseysObj = this.convertToObject(this.newJerseys);
            Object.values(newJerseysObj).forEach(jersey => {
              allJerseys[jersey.id] = jersey;
            });
          }
          
          if (this.oldJerseys) {
            const oldJerseysObj = this.convertToObject(this.oldJerseys);
            Object.values(oldJerseysObj).forEach(jersey => {
              allJerseys[jersey.id] = jersey;
            });
          }
          
          if (Object.keys(allJerseys).length === 0) {
            return this.jerseys || [];
          }
          
          return Object.values(allJerseys);
        }
        
        return this.displayJerseys || {};
      },

      startGuideTour() {
        console.log('Starting guide tour...');
        this.$nextTick(() => {
          const ageGroupElement = document.querySelector('#age-group-size-breakdown');
          const applyButtonElement = document.querySelector('#apply-team-sizes');
          const newProductsTab = document.querySelector('#new-products-tab');
          const oldProductsTab = document.querySelector('#old-products-tab');

          if (!ageGroupElement || !applyButtonElement) {
            console.error('Required elements not found for guide tour');
            return;
          }

          const steps = [
            {
              element: '#age-group-size-breakdown',
              popover: {
                title: this.customer.translations.guide_age_group_title,
                description: this.customer.translations.guide_age_group_description
              }
            },
            {
              element: '#apply-team-sizes',
              popover: {
                title: this.customer.translations.guide_apply_team_sizes_title,
                description: this.customer.translations.guide_apply_team_sizes_description
              }
            }
          ];

          if (newProductsTab && oldProductsTab) {
            steps.unshift(
              {
                element: '#new-products-tab',
                popover: {
                  title: this.customer.translations.guide_new_products_title,
                  description: this.customer.translations.guide_new_products_description
                }
              },
              {
                element: '#old-products-tab',
                popover: {
                  title: this.customer.translations.guide_old_products_title,
                  description: this.customer.translations.guide_old_products_description
                }
              }
            );
          }
          const driverObj = driver({
            steps: steps
          });

          driverObj.drive();
        });
      }
    }
  };
</script>
